import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BackendUser } from '../utils/api';
import { fetchSalesUsers } from '../utils/api';

interface AssignConversationModalProps {
  open: boolean;
  onClose: () => void;
  onAssign: (userId: string) => Promise<void>;
  conversationName?: string;
  users?: BackendUser[];
}

const AssignConversationModal: React.FC<AssignConversationModalProps> = ({
  open,
  onClose,
  onAssign,
  conversationName,
  users: propUsers,
}) => {
  const theme = useTheme();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState<BackendUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (propUsers && propUsers.length > 0) {
        setUsers(propUsers);
        setLoading(false);
      } else {
        loadUsers();
      }
    }
  }, [open, propUsers]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const salesUsers = await fetchSalesUsers();
      setUsers(salesUsers);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) return;

    setAssigning(true);
    setError(null);
    try {
      await onAssign(selectedUserId);
      setSelectedUserId('');
      onClose();
    } catch (err) {
      console.error('Erro ao atribuir conversa:', err);
      setError('Erro ao atribuir conversa');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (!assigning) {
      setSelectedUserId('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Atribuir Conversa
        </Typography>
        {conversationName && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
            Conversa: {conversationName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Vendedor</InputLabel>
            <Select
              value={selectedUserId}
              label="Vendedor"
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={assigning}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={assigning}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            px: 3,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleAssign}
          disabled={!selectedUserId || assigning}
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            px: 3,
            backgroundColor: '#0ea5e9',
            '&:hover': {
              backgroundColor: '#0284c7',
            },
          }}
        >
          {assigning ? <CircularProgress size={20} /> : 'Atribuir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignConversationModal;
